import type {
  ExpiredSubscriptionSummary,
  SubscriptionObject,
} from 'src/lib/services/api/subscription-api/subscription-client.types';

// Months per billing interval, for the monthly price the meta line shows (#2780: "${price}/mo")
const MONTHS_PER_INTERVAL: Record<string, number> = {
  monthly: 1,
  quarterly: 3,
  annually: 12,
};

// Whole dollars as-is, anything with cents to two decimals: $1,399 or $1,199.67
export const formatPrice = (price: number) => {
  const cents = !Number.isInteger(Math.round(price * 100) / 100);

  return `$${price.toLocaleString('en-US', {
    minimumFractionDigits: cents ? 2 : 0,
    maximumFractionDigits: 2,
  })}`;
};

/**
 * The meta line under Reactivate (#2780): `{Plan} · ${price}/mo · {Brand} ending {last4}`, at
 * the monthly price whatever the billing interval. Every part that's missing drops out with
 * its separator. Never "ending undefined", "$NaN" or a trailing " · ". Null when there's
 * neither plan nor price.
 */
export const formatExpiredMetaLine = (
  summary: ExpiredSubscriptionSummary | undefined,
): string | null => {
  if (!summary) {
    return null;
  }

  const parts: string[] = [];
  const months = MONTHS_PER_INTERVAL[summary.interval ?? 'monthly'];

  if (summary.plan) {
    parts.push(summary.plan);
  }
  if (summary.price !== null && Number.isFinite(summary.price) && summary.price > 0 && months) {
    // Whole dollars when it's a quarterly or annual price spread per month, the same figure the
    // plan picker shows for it; a monthly price stays exact
    const perMonth = months === 1 ? summary.price : Math.round(summary.price / months);
    parts.push(`${formatPrice(perMonth)}/mo`);
  }
  if (!parts.length) {
    return null;
  }
  if (summary.card?.brand && summary.card?.last4) {
    parts.push(`${summary.card.brand} ending ${summary.card.last4}`);
  }

  return parts.join(' · ');
};

// The plans the Change plan picker offers: editing plans only. Ben (AI only) and Platform Plan
// never appear, so the picker is not a third way to buy Platform Plan (Toma, 22 Oct).
const EDITING_PLAN_SLUGS = ['scale', 'pro', 'in_house_team'];
// The switch over the cards (Birute, 29 Oct): starts on Quarterly like the website, and says
// "Quarterly", never "Every 3 months"
export const PLAN_INTERVALS = [
  { id: 'monthly', label: 'Monthly' },
  { id: 'quarterly', label: 'Quarterly' },
  { id: 'annually', label: 'Yearly' },
] as const;
export type PlanInterval = (typeof PLAN_INTERVALS)[number]['id'];
export const DEFAULT_PLAN_INTERVAL: PlanInterval = 'quarterly';

const BILLED: Record<PlanInterval, string> = {
  monthly: 'every month',
  quarterly: 'quarterly',
  annually: 'yearly',
};

// One plan card at the chosen interval. Copy and icons live with the cards; this is the money.
export type PlanCardPrice = {
  slug: string;
  priceId: string;
  // Per month, whole dollars as quoted on the website: "$999"
  perMonth: string;
  // "Billed every month" or "$2,997 billed quarterly"
  billed: string;
  // Against the plan's monthly price: "Save 17% vs monthly". Null on monthly or when nothing's saved
  saving: string | null;
};

type PickerPlan = {
  slug: string;
  title: string;
  prices: Record<string, { price_id: string; price: number } | undefined>;
};

/**
 * The plan cards at one billing interval, always Scale, Pro, In House Team in that order. A
 * plan with no price at this interval drops out; Ben and Platform Plan never appear.
 */
export const planCardPrices = (
  plans: PickerPlan[] | undefined,
  interval: PlanInterval,
): PlanCardPrice[] =>
  EDITING_PLAN_SLUGS.flatMap((slug) => {
    const plan = (plans ?? []).find((candidate) => candidate.slug === slug);
    const price = plan?.prices?.[interval];
    const months = MONTHS_PER_INTERVAL[interval];
    if (!price?.price_id || !(price.price > 0)) {
      return [];
    }
    const perMonth = Math.round(price.price / months);
    const monthly = plan?.prices?.monthly?.price;
    const saving =
      months > 1 && monthly && monthly > 0 ? Math.round((1 - perMonth / monthly) * 100) : 0;

    return [
      {
        slug,
        priceId: price.price_id,
        perMonth: formatPrice(perMonth),
        billed:
          months === 1
            ? 'Billed every month'
            : `${formatPrice(price.price)} billed ${BILLED[interval]}`,
        saving: saving > 0 ? `Save ${saving}% vs monthly` : null,
      },
    ];
  });

/**
 * Which card is the client's own plan, and what happens to their old price (Birute, 29 Oct).
 * Reactivate keeps the price for a failed payment, and for a cancelled company whose price is
 * still the one sold; otherwise they come back at today's. The note is about the client's own
 * billing interval, so it's only shown on that interval (`interval`). Null note when the plan or
 * a price is unknown, so nothing half-true is shown.
 */
export const currentPlanNote = (
  summary: ExpiredSubscriptionSummary | undefined,
  plans: PickerPlan[] | undefined,
): { slug: string; interval: string | null; note: string | null } | null => {
  // By slug from the server; by name when the server doesn't send one yet
  const slug =
    summary?.plan_slug ??
    (plans ?? []).find((plan) => summary?.plan && plan.title === summary.plan)?.slug;
  if (!summary || !slug || !EDITING_PLAN_SLUGS.includes(slug)) {
    return null;
  }

  const interval = summary.interval ?? null;
  const known = PLAN_INTERVALS.find((option) => option.id === interval)?.id;
  const paid = summary.paid_price;
  const today = known
    ? (plans ?? []).find((plan) => plan.slug === slug)?.prices?.[known]?.price
    : undefined;
  if (!known || !paid || !(paid > 0) || !today) {
    return { slug, interval, note: null };
  }
  if (Math.abs(paid - today) < 0.5) {
    return { slug, interval, note: 'Same price as before.' };
  }

  const months = MONTHS_PER_INTERVAL[known];
  const was = `${formatPrice(months === 1 ? paid : Math.round(paid / months))}/mo${
    months === 1 ? '' : `, billed ${BILLED[known]}`
  }`;

  return {
    slug,
    interval,
    note: summary.keeps_price
      ? `Reactivate keeps your price: ${was}.`
      : `You paid ${was}. Coming back is at today's price.`,
  };
};

/**
 * The subscription that's paid up but set to end (pending cancellation), for the note on the
 * plan picker: buying a different plan means holding both until the old one ends (#2781).
 */
export const findPendingCancellation = (
  subscriptions: SubscriptionObject[] | undefined,
  now: Date = new Date(),
): { plan: string; endsAt: Date } | null => {
  for (const subscription of subscriptions ?? []) {
    const endsAt = subscription.ends_at ? new Date(subscription.ends_at) : null;
    // The plan, not an addon that happens to come first
    const plan = subscription.products?.find(
      (product) => product.type === 'plan' && product.title,
    )?.title;

    if (
      ['active', 'trialing'].includes(subscription.status) &&
      endsAt &&
      endsAt.getTime() > now.getTime() &&
      plan
    ) {
      return { plan, endsAt };
    }
  }

  return null;
};
